import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useExtraditionMlaWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { ExtraditionMlaWizardDraft } from '../../../types/extraditionMlaCaseRequest';
import type { RequiredErrors } from '../../../utils/requiredValidation';
import { WIZARD_BASE, isWizardCategorizationEditable, wizardReviewPath } from '../../../utils/wizardCategoryRoute';
import { StepBar, StepSep, WizardStep } from '../WizardComponents';
import { wizardStepBarLabel, wizardStepTitle } from '../wizardI18nHelpers';
import { ExtraditionMlaCaseCategorizationCard } from './ExtraditionMlaCaseCategorizationCard';
import { EXTRADITION_MLA_WIZARD_STEPS, type ExtraditionMlaWizardStep } from './extraditionMlaConstants';
import { useRole } from '../../../rbac/hooks';
import { tasksListPathForRole } from '../../../rbac/tasksListPath';

export function extraditionMlaWizardPath(step: ExtraditionMlaWizardStep, caseRequestId?: string): string {
  const base = `${WIZARD_BASE}/step-${String(step)}`;
  const id = (caseRequestId ?? '').trim();
  return id ? `${base}/${id}` : base;
}

export function extraditionMlaReviewPath(caseRequestId?: string): string {
  return wizardReviewPath(undefined, caseRequestId);
}

export function ExtraditionMlaWizardShell({
  currentStep,
  draft,
  setDraft,
  categorizationEditable = true,
  categorizationErrors,
  onClearCategorizationError,
  children,
}: {
  currentStep: ExtraditionMlaWizardStep;
  draft: ExtraditionMlaWizardDraft;
  setDraft?: React.Dispatch<React.SetStateAction<ExtraditionMlaWizardDraft>>;
  categorizationEditable?: boolean;
  categorizationErrors?: RequiredErrors;
  onClearCategorizationError?: (key: string) => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { roleCode } = useRole();
  const { setDraft: contextSetDraft } = useExtraditionMlaWizardContext();
  const effectiveSetDraft = setDraft ?? contextSetDraft;
  const showWizard = Boolean(draft.fileCategoryId);

  return (
    <>
      <div className="bc">
        <Link to={tasksListPathForRole(roleCode)}>{t('nav.tasksAndCases')}</Link>
        <span className="bc-sep">/</span>
        {t('wizard.createCaseRequest')}
      </div>

      <div className="pg-title">{t('wizard.createCaseRequest')}</div>
      <div className="pg-sub" style={{ marginBottom: 16 }}>
        {t('wizard.createSubtitle')}
      </div>

      <ExtraditionMlaCaseCategorizationCard
        draft={draft}
        setDraft={effectiveSetDraft}
        editable={isWizardCategorizationEditable(categorizationEditable, draft.draftRequestId)}
        errors={categorizationErrors}
        onClearError={onClearCategorizationError}
      />

      {showWizard ? (
        <>
          <StepBar>
            {EXTRADITION_MLA_WIZARD_STEPS.map((s, idx) => {
              const state = currentStep > s.n ? 'done' : currentStep === s.n ? 'active' : 'pending';
              return (
                <div key={s.n} style={{ display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                  <WizardStep
                    state={state}
                    numberOrCheck={state === 'done' ? '✓' : String(s.n)}
                    stepLabel={wizardStepBarLabel(t, s.n)}
                    name={wizardStepTitle(t, 'extraditionMla', s.n)}
                    clickable={false}
                  />
                  {idx < EXTRADITION_MLA_WIZARD_STEPS.length - 1 ? <StepSep /> : null}
                </div>
              );
            })}
          </StepBar>

          {children}
        </>
      ) : null}
    </>
  );
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }} role="alert">
      {message}
    </div>
  );
}

export function inpClass(hasError: boolean): string {
  return `ccr-inp${hasError ? ' inv' : ''}`;
}
